import {
  Briefcase,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Edit,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { getInitials } from "@/utils";
import { Avatar } from "@/components/ui/avatar";
import { CandidateActionsDropdown } from "./CandidateActionsDropdown";
import type { ProfileDetail } from "@/interface/profile";
import type { ProfileSkillDetail } from "@/interface/profileSkill";

interface CandidateHeaderProps {
  profile: ProfileDetail;
  skillsList: ProfileSkillDetail[];
  totalSkills?: number;
}

export function CandidateHeader({
  profile,
  skillsList,
  totalSkills,
}: CandidateHeaderProps) {
  const navigate = useNavigate();

  const totalSkillsCount =
    typeof totalSkills === "number" ? totalSkills : skillsList.length;

  const fullName = profile
    ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
    profile.username
    : "Candidate Profile";

  return (
    <div className="bg-[#1a0b2e] rounded-2xl overflow-hidden relative border border-purple-500/20 shadow-xl">
      <div className="absolute inset-0 bg-[url('/candidate-bg.jpg')] bg-cover bg-center opacity-40 mix-blend-screen" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1a0b2e] via-[#1a0b2e]/90 to-transparent" />

      <div className="relative p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        <div className="flex items-start gap-6">
          <div className="relative">
            <Avatar
              src={profile?.profile_picture}
              name={fullName}
              className="h-28 w-28 rounded-2xl border-4 border-[#1a0b2e] shadow-lg"
              fallbackClassName="text-3xl"
            />
          </div>

          <div className="text-white space-y-2">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              {fullName}
            </h1>
            <div className="flex items-center gap-4 text-purple-200 text-sm">
              <span className="font-medium">
                {profile?.employee_code ||
                  profile?.employee_id ||
                  `EMP-${profile?.id || "001"}`}
              </span>
              <span className="text-slate-400">|</span>
              <span>{profile?.job_role || "Candidate"}</span>
              <span className="text-slate-400">|</span>
              <span>
                {profile?.department_detail?.department_name ||
                  "Department N/A"}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-purple-300 pt-1">
              <span className="flex items-center gap-1">
                <Briefcase className="h-3 w-3" />{" "}
                {profile?.department_detail?.department_name ||
                  "Department N/A"}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />{" "}
                {profile?.experience_years ?? 0}y{" "}
                {profile?.experience_months ?? 0}m Experience
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />{" "}
                {profile?.work_location ||
                  (profile?.city
                    ? `${profile.city}, ${profile.country || ""}`
                    : "Location N/A")}
              </span>
            </div>

            <div className="flex gap-2 pt-2 flex-wrap">
              {skillsList
                .slice(0, 5)
                .map((sk: ProfileSkillDetail, idx: number) => (
                  <span
                    key={sk.id || idx}
                    className="px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/30 text-xs font-medium text-purple-200"
                  >
                    {sk.skill_name}
                  </span>
                ))}
              {totalSkillsCount > 5 && (
                <span className="px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/30 text-xs font-medium text-purple-200">
                  +{totalSkillsCount - 5}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 self-start lg:self-auto">
          <Button
            onClick={() => navigate(`/candidates/${profile?.id}/edit`)}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold border-0"
          >
            <Edit className="h-4 w-4 mr-2" /> Edit Profile
          </Button>
          <CandidateActionsDropdown profile={profile} />
        </div>
      </div>
    </div>
  );
}
